import { computeTotalFrames, timeSecondsForFrame } from '@/rendering/export/frameTime'
import type {
  EncoderFrameSink,
  ExportRenderConfig,
  RenderPipelineResult,
  RenderProgress,
  SceneFrameAdapter,
} from '@/rendering/export/types'

/**
 * 让出主线程一帧，避免长时间 `render` 循环卡死 UI 与 Worker。
 * 在 Node 单测环境下无 `requestAnimationFrame` 时退回 `setTimeout(0)`。
 */
export function yieldToHostFrame(): Promise<void> {
  return new Promise((resolve) => {
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => resolve())
    } else {
      setTimeout(() => resolve(), 0)
    }
  })
}

/**
 * 执行离线逐帧渲染：frameIndex → currentTime → 场景 → 编码器。
 *
 * @param signal - 取消信号
 * @param config - 导出渲染配置
 * @param scene - 场景适配器
 * @param sink - 编码输出适配器
 * @param onProgress - 可选进度（基于帧索引）
 */
export async function runOfflineRenderPipeline(
  signal: AbortSignal,
  config: ExportRenderConfig,
  scene: SceneFrameAdapter,
  sink: EncoderFrameSink,
  onProgress?: (p: RenderProgress) => void,
): Promise<RenderPipelineResult> {
  const totalFrames = computeTotalFrames(config.durationSec, config.fps)
  const fpsInt = Math.max(1, Math.round(Number(config.fps)))
  let sessionOpen = false

  try {
    onProgress?.({ frameIndex: 0, totalFrames, phase: 'prepare' })
    await scene.beginSession(config)
    sessionOpen = true

    const canvas = scene.getCanvas()
    if (!canvas) {
      throw new Error('Scene adapter returned no canvas')
    }

    await sink.beginEncoding(config, totalFrames, canvas)

    for (let i = 0; i < totalFrames; i++) {
      if (signal.aborted) {
        await sink.abortEncoding()
        return { status: 'cancelled' }
      }

      const timeSeconds = timeSecondsForFrame(i, config.durationSec, config.fps)
      const desc = {
        frameIndex: i,
        totalFrames,
        timeSeconds,
        width: config.width,
        height: config.height,
        fps: fpsInt,
      }

      await scene.applyFrame(desc)
      await sink.consumeFrame(desc)
      onProgress?.({ frameIndex: i + 1, totalFrames, phase: 'render' })
      await yieldToHostFrame()
    }

    onProgress?.({ frameIndex: totalFrames, totalFrames, phase: 'finalize' })
    const blob = await sink.finishEncoding()
    if (!blob) {
      throw new Error('Encoder produced no output')
    }
    return { status: 'ok', blob }
  } catch (e) {
    try {
      await sink.abortEncoding()
    } catch {
      /* ignore */
    }
    const err = e instanceof Error ? e : new Error(String(e))
    return { status: 'error', error: err }
  } finally {
    if (sessionOpen) {
      try {
        await scene.endSession()
      } catch {
        /* ignore */
      }
    }
  }
}
